import {
  type CreateProductionReq,
  type ProductionPrefillItem,
  type ProductionPrefillRes,
} from "@artisancode/api-types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { getErrorMessage } from "@/lib/error-message";
import { newIdempotencyKey } from "@/lib/idempotency";
import { productService } from "@/services/product";
import { stockTransformationService } from "@/services/stock-transformation";

export interface EditableIngredient {
  rawMaterialId: string;
  rawMaterialName: string;
  rawMaterialSku?: string | null;
  uomId: string;
  uomName: string;
  recipeQty: number;
  requiredQty: number;
  qtyStr: string;
  unitCost: number;
  subtotalCost: number;
  availableStock: number;
  isShortage: boolean;
}

/** One menu in the batch, with the ingredients prefilled from its active recipe. */
export interface MenuDraft {
  key: string;
  productId: string;
  targetYieldStr: string;
  ingredients: EditableIngredient[];
  prefillMeta: ProductionPrefillRes | null;
  isLoadingPrefill: boolean;
  prefillError: string | null;
}

const today = () => new Date().toISOString().slice(0, 10);

const newMenu = (): MenuDraft => ({
  key: crypto.randomUUID(),
  productId: "",
  targetYieldStr: "10",
  ingredients: [],
  prefillMeta: null,
  isLoadingPrefill: false,
  prefillError: null,
});

const menuCost = (m: MenuDraft) =>
  m.ingredients.reduce((sum, i) => sum + i.subtotalCost, 0);

/**
 * Menus can share an ingredient (cups, beans), so the bar stock is checked
 * against the batch's total need, not per menu.
 */
function markSharedShortage(menus: MenuDraft[]): MenuDraft[] {
  const totalNeed = new Map<string, number>();
  const keyOf = (i: EditableIngredient) => `${i.rawMaterialId}:${i.uomId}`;
  for (const m of menus) {
    for (const i of m.ingredients) {
      totalNeed.set(
        keyOf(i),
        (totalNeed.get(keyOf(i)) ?? 0) + (Number(i.qtyStr) || 0),
      );
    }
  }
  return menus.map((m) => ({
    ...m,
    ingredients: m.ingredients.map((i) => ({
      ...i,
      isShortage: (totalNeed.get(keyOf(i)) ?? 0) > i.availableStock,
    })),
  }));
}

/** State, recipe prefill, costing and submit logic for the production batch dialog. */
export function useProductionForm({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const [date, setDate] = useState(today);
  const [menus, setMenus] = useState<MenuDraft[]>(() => [newMenu()]);
  const [generalNote, setGeneralNote] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Latest prefill request per menu: a slower, older response must not overwrite a newer one.
  const prefillRequest = useRef(new Map<string, number>());

  // Fetch produced products
  const { data: productsRes, isLoading: isLoadingProducts } = useQuery({
    queryKey: ["products", "produced-for-production"],
    queryFn: () => productService.list({ per_page: 200 }),
    enabled: open,
  });

  const producedProducts = useMemo(() => {
    return (productsRes?.items || []).filter(
      (p) => p.sourcingType === "produced" && p.isActive,
    );
  }, [productsRes]);

  // Reset on modal open
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDate(today());
      setMenus([newMenu()]);
      setGeneralNote("");
    }
  }, [open]);

  const patchMenu = (key: string, patch: Partial<MenuDraft>) =>
    setMenus((prev) =>
      markSharedShortage(
        prev.map((m) => (m.key === key ? { ...m, ...patch } : m)),
      ),
    );

  const loadPrefill = (key: string, productId: string, yieldStr: string) => {
    const yieldNum = Number(yieldStr);
    if (!productId || isNaN(yieldNum) || yieldNum <= 0) {
      patchMenu(key, {
        ingredients: [],
        prefillMeta: null,
        prefillError: null,
      });
      return;
    }
    const requestId = (prefillRequest.current.get(key) ?? 0) + 1;
    prefillRequest.current.set(key, requestId);
    const isLatest = () => prefillRequest.current.get(key) === requestId;
    patchMenu(key, { isLoadingPrefill: true, prefillError: null });

    stockTransformationService
      .prefillProduction({ product_id: productId, yield: yieldNum })
      .then((res) => {
        if (!isLatest()) return;
        patchMenu(key, {
          prefillMeta: res,
          ingredients: res.items.map((i: ProductionPrefillItem) => ({
            ...i,
            qtyStr: i.requiredQty.toString(),
          })),
          isLoadingPrefill: false,
        });
      })
      .catch((err: Error) => {
        if (!isLatest()) return;
        patchMenu(key, {
          ingredients: [],
          prefillMeta: null,
          isLoadingPrefill: false,
          prefillError: getErrorMessage(
            err,
            "Resep aktif untuk produk ini belum dibuat di Master Produk.",
          ),
        });
      });
  };

  const setMenuProduct = (key: string, productId: string) => {
    const menu = menus.find((m) => m.key === key);
    patchMenu(key, { productId });
    loadPrefill(key, productId, menu?.targetYieldStr ?? "");
  };

  const setMenuYield = (key: string, targetYieldStr: string) => {
    const menu = menus.find((m) => m.key === key);
    patchMenu(key, { targetYieldStr });
    loadPrefill(key, menu?.productId ?? "", targetYieldStr);
  };

  const addMenu = () => setMenus((prev) => [...prev, newMenu()]);

  const removeMenu = (key: string) => {
    prefillRequest.current.delete(key);
    setMenus((prev) => markSharedShortage(prev.filter((m) => m.key !== key)));
  };

  // Update ingredient actual qty
  const changeQty = (key: string, idx: number, val: string) => {
    const menu = menus.find((m) => m.key === key);
    const row = menu?.ingredients[idx];
    if (!menu || !row) return;
    const num = Number(val);
    // Hitung subtotal eksak tanpa pembulatan prematur
    const subtotalCost = !isNaN(num) && num > 0 ? num * row.unitCost : 0;
    const ingredients = [...menu.ingredients];
    ingredients[idx] = { ...row, qtyStr: val, subtotalCost };
    patchMenu(key, { ingredients });
  };

  const menuSummary = (m: MenuDraft) => {
    const totalInputCost = menuCost(m);
    const targetYield = Number(m.targetYieldStr) || 1;
    // HPP per unit eksak
    const costPerUnit = targetYield > 0 ? totalInputCost / targetYield : 0;
    const product = producedProducts.find((p) => p.id === m.productId);
    const sellingPrice = product?.sellingPrice || 0;
    const grossMarginPercent =
      sellingPrice > 0
        ? Math.round(((sellingPrice - costPerUnit) / sellingPrice) * 100)
        : null;
    return {
      productName: product?.name,
      totalInputCost,
      costPerUnit,
      sellingPrice,
      grossMarginPercent,
      hasShortage: m.ingredients.some((i) => i.isShortage),
    };
  };

  const totalInputCost = menus.reduce((sum, m) => sum + menuCost(m), 0);
  const isLoadingPrefill = menus.some((m) => m.isLoadingPrefill);
  const isReady =
    !isLoadingPrefill &&
    menus.every((m) => m.prefillMeta && m.ingredients.length > 0);
  const takenProductIds = new Set(
    menus.map((m) => m.productId).filter(Boolean),
  );

  const payload: CreateProductionReq = {
    date,
    outputs: menus.map((m) => ({
      productId: m.productId,
      targetYieldQty: Number(m.targetYieldStr),
      inputs: m.ingredients.map((i) => ({
        rawMaterialId: i.rawMaterialId,
        qty: Number(i.qtyStr),
        uomId: i.uomId,
      })),
    })),
    note: generalNote || undefined,
  };
  const payloadSignature = JSON.stringify(payload);
  // One key per opened dialog and form content: a retry of the same batch after a
  // network error reuses it, so the server returns the draft it already created;
  // changing the batch (e.g. after a shortage) gets a new key, not the stale draft.
  const idempotencyKey = useMemo(
    () => newIdempotencyKey("prod"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, payloadSignature],
  );

  const submit = async (autoComplete: boolean) => {
    for (const m of menus) {
      const name = menuSummary(m).productName;
      if (!m.productId) {
        toast.error("Pilih produk untuk setiap menu");
        return;
      }
      const yieldNum = Number(m.targetYieldStr);
      if (!yieldNum || yieldNum <= 0) {
        toast.error(`Target jumlah produksi ${name} harus lebih dari 0`);
        return;
      }
      if (m.ingredients.length === 0) {
        toast.error(`Bahan baku ${name} belum dimuat dari resep`);
        return;
      }
      for (const item of m.ingredients) {
        const num = Number(item.qtyStr);
        if (isNaN(num) || num <= 0) {
          toast.error(
            `Jumlah bahan "${item.rawMaterialName}" (${name}) harus lebih besar dari 0`,
          );
          return;
        }
      }
    }

    try {
      setIsSubmitting(true);

      const created = await stockTransformationService.createProduction(
        payload,
        idempotencyKey,
      );

      if (autoComplete) {
        try {
          // Every menu at its target: the server defaults a missing actual qty to the target.
          await stockTransformationService.complete(created.id);
        } catch (err) {
          // The draft is saved; show it in the list so it can be completed or cancelled there.
          onSuccess();
          toast.error(
            `Draft ${created.code} tersimpan, tapi gagal diselesaikan: ${getErrorMessage(err, "coba lagi")}`,
          );
          return;
        }
        toast.success(
          `Batch ${created.code} berhasil diproduksi & masuk ke stok!`,
        );
      } else {
        toast.success(
          `Draft batch produksi ${created.code} berhasil disimpan!`,
        );
      }

      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Gagal membuat batch produksi"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    date,
    setDate,
    generalNote,
    setGeneralNote,
    menus,
    addMenu,
    removeMenu,
    setMenuProduct,
    setMenuYield,
    changeQty,
    menuSummary,
    takenProductIds,
    totalInputCost,
    isLoadingPrefill,
    isReady,
    isSubmitting,
    isLoadingProducts,
    producedProducts,
    submit,
  };
}
